import { createContext, useContext } from "react"

import { useQuery } from "@tanstack/react-query"

import type { RouterOutput } from "lib/trpc"
import { isUnauthorized, useTRPC } from "lib/trpc"

export type Me = RouterOutput["admin"]["me"]

/**
 * Raw session query. Only for code that renders outside the auth gate
 * (AuthProvider, AppReflagProvider) — everything under a page uses
 * {@link useMe}, which is guaranteed to resolve.
 */
export function useMeQuery() {
  const trpc = useTRPC()
  const { data: me, isPending } = useQuery(
    trpc.admin.me.queryOptions(undefined, {
      // Mirrors the old Apollo cache-first behavior: fetch once per session.
      // Freshness is driven explicitly by login (invalidate) and logout (clear).
      staleTime: Infinity,
      // A 401 means "not logged in" — retrying only delays the /login redirect.
      retry: (count, error) => !isUnauthorized(error) && count < 3,
    }),
  )
  return { me, isPending }
}

export const MeContext = createContext<Me | null>(null)

/**
 * The logged-in admin. Guaranteed resolved and non-null — AuthProvider
 * gates every non-public route and provides the value.
 */
export function useMe(): { me: Me } {
  const me = useContext(MeContext)
  if (!me) {
    throw new Error("useMe must be used under a page wrapped with withAuth")
  }
  return { me }
}
